import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { displayArchivoNombre } from "../utils/archivoDisplayName";
import { getMessage } from "../data/locales";
import CourseMaterialInlineViewer from "./CourseMaterialInlineViewer";
import "./CourseMaterialEmbedModal.css";

const FOCUSABLE_SELECTOR =
  "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

/**
 * @param {{ open: boolean, archivo: { nombre?: string, storageKey?: string, url?: string } | null, onClose: () => void, canDownload?: boolean }} props
 */
function CourseMaterialEmbedModal({ open, archivo, onClose, canDownload = false }) {
  const modalRef = useRef(null);
  const previousActiveRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previousActiveRef.current = document.activeElement;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    const raf = requestAnimationFrame(() => {
      const first = modalRef.current?.querySelector(FOCUSABLE_SELECTOR);
      (first || modalRef.current)?.focus();
    });
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      cancelAnimationFrame(raf);
      previousActiveRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open || !archivo) return null;

  const title =
    displayArchivoNombre(archivo, "") || getMessage("campus.materialViewerTitle");

  return createPortal(
    <div
      className="course-material-embed-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="course-material-embed-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="course-material-embed-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="course-material-embed-header">
          <h2 id="course-material-embed-title">{title}</h2>
          <button type="button" className="course-material-embed-close" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="course-material-embed-body">
          <CourseMaterialInlineViewer archivo={archivo} canDownload={canDownload} />
        </div>
      </div>
    </div>,
    document.body
  );
}

CourseMaterialEmbedModal.propTypes = {
  open: PropTypes.bool.isRequired,
  archivo: PropTypes.shape({
    nombre: PropTypes.string,
    storageKey: PropTypes.string,
    url: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  canDownload: PropTypes.bool,
};

CourseMaterialEmbedModal.defaultProps = {
  archivo: null,
  canDownload: false,
};

export default CourseMaterialEmbedModal;
